//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { parseSocialText } from '@utils/stringUtils';

import _ from "js/underscore-mixins";

(function () {
  /**
   * Component used to parse a body of text such that all @mentions and #hashtags become links.
   * Note: Emoji filter is also applied for good measure.
   *
   * @param {string} text - The text to filter (usually a caption/comment).
   * @param {string} network - The social network the body of text comes from. Used to apply appropriate regex to mention/hashtag match.
   * @param {object} clickHandlers - A set of functions used to handle source links. Specifically the object should contain
   * two functions 'user' and 'hashtag' which take in the click $event and an object with a 'name' and 'id' attribute respectively.
   * @param {object} hrefHandlers - A set of functions used to generate hrefs for links. Specifically the object should contain
   * two functions 'user' and 'hashtag' which take in an object with a 'name' and 'id' attribute respectively.
   */
  angular.module('curalate').component('linkedText', {
    template: `
        <span ng-repeat="item in ::$ctrl.textSegments" with-unison>
            <span ng-switch="item.type">
                 <span ng-switch-when="text" ng-bind-html="::item.value" style="white-space: pre-line"></span>
                 <a ng-switch-default
                    ng-attr-target="{{ $ctrl.openInNewTab ? '_blank' : undefined }}"
                    ng-click="::$ctrl.itemClick($event, item)"
                    ng-href="{{ ::$ctrl.generateHrefAttr(item) }}"
                    ng-bind-html="::item.value"
                    class="cu-text-link-primary"
                 ></a>
            </span>
        </span>`,
    bindings: {
      text: '<',
      network: '<',
      clickHandlers: '<',
      hrefHandlers: '<',
      openInNewTab: '<',
    },
    controller: [
      '$filter',
      '$sce',
      'ContentSourceTypes',
      'StringUtils',
      function ($filter, $sce, ContentSourceTypes, StringUtils) {
        const self = this;

        const htmlAndEmojiEncode = (value) =>
          _([value]).map($filter('htmlEncode')).map($filter('emoji')).pop();

        const isHashtag = (item) => item.type === 'hashtag';
        const getCanonicalObj = (item) => {
          const generateUserObj = (username) => ({
            name: username.toLowerCase(),
          });
          const generateTagObj = (hashtag) => ({ id: hashtag.toLowerCase() });
          const generateLinkObj = (link) => ({ link });

          const valueString = item.value.toString();
          switch (item.type) {
            case 'hashtag':
              return generateTagObj(valueString.replace('#', ''));
            case 'link':
              return generateLinkObj(valueString);
            default:
              return generateUserObj(valueString.replace('@', ''));
          }
        };

        const generateHandler = (handlerType, item) => {
          const isClickHandler = handlerType === 'click';
          const contentSourceType = (() => {
            switch (item.type) {
              case 'hashtag':
                return ContentSourceTypes.HASHTAG;
              case 'link':
                return ContentSourceTypes.LINK;
              default:
                return ContentSourceTypes.USER;
            }
          })();

          const handler =
            (isClickHandler
              ? _(self.clickHandlers)
              : _(self.hrefHandlers)
            ).safeGet(contentSourceType) || angular.noop;

          return handler;
        };

        this.$onInit = function () {
          const text = self.text;

          self.generateHrefAttr = function (item) {
            const obj = getCanonicalObj(item);
            const handler = generateHandler('href', item);
            const href = handler(obj);

            return href;
          };

          self.itemClick = function ($event, item) {
            const obj = getCanonicalObj(item);
            const handler = self.openInNewTab
              ? angular.noop
              : generateHandler('click', item);

            handler($event, obj);
          };

          const parsedText = parseSocialText(text);

          self.textSegments = parsedText.map((segment) => {
            return {
              type: segment.type,
              value: htmlAndEmojiEncode(segment.value),
            };
          });
        };
      },
    ],
  });
})();
